import React from 'react';
import {
  Field,
  SelectField,
  ToggleGroup,
  Toggle,
  FieldHint,
} from '@ktag/styleguide/dist/react';
import uniqid from 'uniqid';
import { useTranslation } from 'react-i18next';
import { NewsletterFormField } from 'types/config.types';
import { EMAIL_PATTERN } from 'utils/emailValidationPattern';

interface Props {
  fieldItem: NewsletterFormField;
}

const DynamicFormField = ({ fieldItem }: Props) => {
  const { t } = useTranslation('newsletter');

  const getFieldHint = () => {
    if (fieldItem.newsletterLabels.length === 1) {
      return t('newsletter_dynamic_field_hint_one', {
        newsletterLabels: fieldItem.newsletterLabels[0],
      });
    }
    if (fieldItem.newsletterLabels.length === 2) {
      return t('newsletter_dynamic_field_hint_other', {
        newsletterLabels: `${fieldItem.newsletterLabels[0]} und ${fieldItem.newsletterLabels[1]}`,
      });
    }
    if (fieldItem.newsletterLabels.length > 2) {
      const allButLast = fieldItem.newsletterLabels.slice(0, -1).join(', ');
      const last = fieldItem.newsletterLabels.at(-1);
      return t('newsletter_dynamic_field_hint_other', {
        newsletterLabels: `${allButLast} und ${last}`,
      });
    }
    return null;
  };

  if (fieldItem.type === 'string') {
    return (
      <Field
        label={fieldItem.label}
        name={fieldItem.name}
        required={fieldItem.required}
        multiline={fieldItem.multiline}
        type={fieldItem.name === 'email' ? 'email' : undefined}
        pattern={fieldItem.name === 'email' ? EMAIL_PATTERN : undefined}
        patternErrorMessage={t('newsletter_email_form_pattern_error')}
      >
        {fieldItem.newsletterLabels && <FieldHint text={getFieldHint()} />}
      </Field>
    );
  }
  if (fieldItem.type === 'select') {
    return (
      <SelectField
        label={fieldItem.label}
        name={fieldItem.name}
        required={fieldItem.required}
        options={fieldItem.options}
      >
        {fieldItem.newsletterLabels && <FieldHint text={getFieldHint()} />}
      </SelectField>
    );
  }
  if (fieldItem.type === 'checkbox') {
    return (
      <ToggleGroup
        name={fieldItem.name}
        label={fieldItem.label}
        required={fieldItem.required}
      >
        <Toggle value="on" label={fieldItem.toggleLabel} />
        {fieldItem.newsletterLabels && <FieldHint text={getFieldHint()} />}
      </ToggleGroup>
    );
  }
  if (fieldItem.type === 'radio') {
    return (
      <ToggleGroup name={fieldItem.name} label={fieldItem.label}>
        {fieldItem.options.map((item) => (
          <Toggle type="radio" value={item.value} label={item.label} key={uniqid()} />
        ))}
        {fieldItem.newsletterLabels && <FieldHint text={getFieldHint()} />}
      </ToggleGroup>
    );
  }
  return null;
};

export default DynamicFormField;
